import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useInstructorMode } from '../hooks/useInstructorMode';
import { formatShortDate } from '../utils/dateUtils';
import './PlaylistList.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function PlaylistList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { buildUrl, isInstructorMode, authLoading } = useInstructorMode();
  
  const limit = parseInt(searchParams.get('limit')) || 50;
  const currentOffset = parseInt(searchParams.get('offset')) || 0; 

  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  // Map of playlist_id → { avg, count } for the rating pill. Loaded once
  // on mount via /api/ratings/aggregate (no ids = all rated playlists).
  const [ratings, setRatings] = useState({});
  
  /**
   * Check if a playlist should be visible based on current Mountain Time
   * Playlists are only visible AFTER 10:30 AM Mountain Time on their date.
   */
const isPlaylistVisible = (dateString) => {
  if (!dateString) return false;
  
  // Extract just the date part if it's a full ISO timestamp
  const datePart = dateString.split('T')[0]; // Get "2025-11-05" from "2025-11-05T07:00:00.000Z"
  const [year, month, day] = datePart.split('-');
  
  // Get current time in Mountain Time
  const nowMT = new Date(new Date().toLocaleString('en-US', { 
    timeZone: 'America/Denver'
  }));
  
  // Create threshold: 10:30 AM MT on the playlist date
  // Note: month is 0-indexed in Date constructor
  const playlistThreshold = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 10, 30, 0);
  
  // Compare Date objects directly
  return nowMT >= playlistThreshold;
};

  const fetchPlaylists = useCallback(async (newOffset, reset = false) => {
    // Wait for auth check to complete before fetching
    if (authLoading) return;
    
    try {
      setLoading(true);
      
      // Use backend param (?show=all) when in instructor mode
      let url = `${API_BASE}/playlists?limit=${limit}&offset=${newOffset}`;
      if (isInstructorMode) {
        url += '&show=all';
      }
      
      const response = await axios.get(url);
      
      // Filter playlists based on visibility (10:30 AM MT rule)
      // Only filter if NOT in instructor mode
      const allPlaylists = response.data.playlists;
      const visiblePlaylists = isInstructorMode 
        ? allPlaylists 
        : allPlaylists.filter(playlist => isPlaylistVisible(playlist.playlist_date));
      
      if (reset) {
        setPlaylists(visiblePlaylists);
      } else {
        setPlaylists(prev => [...prev, ...visiblePlaylists]);
      }
      
      setTotal(response.data.total);
      setHasMore(newOffset + limit < response.data.total);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load playlists');
      console.error('Error fetching playlists:', err);
    } finally {
      setLoading(false);
    }
  }, [limit, isInstructorMode, authLoading]);

  useEffect(() => {
    fetchPlaylists(currentOffset, true);
  }, [fetchPlaylists, currentOffset]);

  // Fetch every rated-playlist aggregate once. The endpoint returns only
  // playlists with ≥1 rating, so the response is small even at scale.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE}/ratings/aggregate`)
      .then((r) => { if (!cancelled) setRatings(r.data || {}); })
      .catch(() => { /* best-effort; pill just won't appear */ });
    return () => { cancelled = true; };
  }, []);

  const loadMore = () => {
    if (!loading && hasMore) {
      const newOffset = currentOffset + limit;
      
      setSearchParams(prev => {
        prev.set('offset', newOffset.toString());
        if (isInstructorMode) {
          prev.set('instructor', '');
        }
        return prev;
      });
    }
  };

  if (error) {
    return (
      <div className="playlist-list error">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="playlist-list">
      <div className="playlist-list-header">
        <h2>All Playlists</h2>
        <p className="total-count">{total} total playlists</p>        
      </div>

      <div className="playlists-grid">
        {playlists.map((playlist) => (
          <a
            key={playlist.id}
            href={buildUrl(`/playlist/${playlist.id}`)}
            className="playlist-card"
          >
            <div className="playlist-card-header">
              <h3>{formatShortDate(playlist.playlist_date)}</h3>
              <svg viewBox="0 0 496 512" className="spotify-icon-small" fill="#1DB954">
                <path d="M248 8C111.1 8 0 119.1 0 256s111.1 248 248 248 248-111.1 248-248S384.9 8 248 8zm100.7 364.9c-4.2 0-6.8-1.3-10.7-3.6-62.4-37.6-135-39.2-206.7-24.5-3.9 1-9 2.6-11.9 2.6-9.7 0-15.8-7.7-15.8-15.8 0-10.3 6.1-15.2 13.6-16.8 81.9-18.1 165.6-16.5 237 26.2 6.1 3.9 9.7 7.4 9.7 16.5s-7.1 15.4-15.2 15.4zm26.9-65.6c-5.2 0-8.7-2.3-12.3-4.2-62.5-37-155.7-51.9-238.6-29.4-4.8 1.3-7.4 2.6-11.9 2.6-10.7 0-19.4-8.7-19.4-19.4s5.2-17.8 15.5-20.7c27.8-7.8 56.2-13.6 97.8-13.6 64.9 0 127.6 16.1 177 45.5 8.1 4.8 11.3 11 11.3 19.7-.1 10.8-8.5 19.5-19.4 19.5zm31-76.2c-5.2 0-8.4-1.3-12.9-3.9-71.2-42.5-198.5-52.7-280.9-29.7-3.6 1-8.1 2.6-12.9 2.6-13.2 0-23.3-10.3-23.3-23.6 0-13.6 8.4-21.3 17.4-23.9 35.2-10.3 74.6-15.2 117.5-15.2 73 0 149.5 15.2 205.4 47.8 7.8 4.5 12.9 10.7 12.9 22.6 0 13.6-11 23.3-23.2 23.3z"/>
              </svg>
            </div>
            <div className="playlist-card-footer">
              {ratings[playlist.id] && (
                <span className="rating-line">
                  ★ {ratings[playlist.id].avg.toFixed(1)}{' '}
                  <span className="rating-line-n">({ratings[playlist.id].count})</span>
                </span>
              )}
              {playlist.is_sold_out && (
                <span className="sold-out-badge">Sold Out</span>
              )}
            </div>
          </a>
        ))}
      </div>

      {loading && currentOffset === 0 && (
        <div className="loading-initial">
          <div className="spinner"></div>
          <p>Loading playlists...</p>
        </div>
      )}

      {hasMore && !loading && (
        <div className="load-more-container">
          <button onClick={loadMore} className="btn-load-more">
            Load More
          </button>
        </div>
      )}

      {loading && currentOffset > 0 && (
        <div className="loading-more">
          <div className="spinner-small"></div>
        </div>
      )}

      {!hasMore && playlists.length > 0 && (
        <div className="end-message">
          <p>You've reached the end! 🎉</p>
        </div>
      )}
    </div>
  );
}

export default PlaylistList;